import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Search, Handshake, FileCheck, Users2, CheckCircle2, XCircle, ChevronRight } from "lucide-react";
import CTABand from "@/components/recruit/CTABand";

const WORKFLOW_STEPS = [
  { num: "01", icon: Search, title: "物件ソーシング", desc: "ホテルとして運営できるポテンシャルのある物件を探します。直接アプローチ、不動産ネットワーク、外部パートナーの活用など、方法は自由。自分なりのパイプラインを構築していきます。" },
  { num: "02", icon: Handshake, title: "オーナー面談・提案", desc: "物件オーナーとの面談を設定し、ACOEのブランド運営モデルを提案。実績データ、収益シミュレーション、250%保証など、説得力のある材料は揃っています。" },
  { num: "03", icon: FileCheck, title: "契約締結", desc: "条件交渉から契約締結まで。転貸契約または運営代行契約の2モデルから、オーナーに最適なプランを提案します。代表と相談しながら進められます。" },
  { num: "04", icon: Users2, title: "リレーション構築", desc: "契約後も、オーナーとの関係を維持。運営状況の報告やコミュニケーションを通じて、長期的な信頼関係を築いていきます。次の物件紹介につながることも。" },
];

const FIT_YES = [
  "関係構築型の営業が得意、または好きな方",
  "自分でやり方を考え、行動に移せる方",
  "ホスピタリティ・不動産・B2B営業の経験がある方",
  "キャリアアップに意欲的で、成長機会を活かしたい方",
  "柔軟な環境で自分のペースを大切にしながら成果を出したい方",
];

const FIT_NO = [
  "明確なマニュアルや指示がないと動けない方",
  "短期的な数字だけを追いたい方",
  "リモートワーク中心の働き方が合わない方",
  "物事をネガティブに捕らえがちな方",
  "考えるだけで行動に移せない方",
];

export default function WorkPage() {
  useEffect(() => { document.title = "仕事について | ACOE 採用サイト"; }, []);

  return (
    <div data-testid="work-page">
      {/* ─── HERO ─── */}
      <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-acoe-charcoal" data-testid="work-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-6">What We Do</p>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.15] max-w-3xl">
            ACOEの仕事
          </h1>
          <p className="mt-8 text-white/60 text-base md:text-lg max-w-2xl leading-relaxed">
            ACOEの成長を支えるのは、物件の開拓と運営の2つの柱。
            ここでは、現在募集中の物件開拓営業について詳しくご紹介します。
          </p>
        </div>
      </section>

      {/* ─── ROLE MISSION ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="work-mission">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Role Mission</p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-8">
              物件開拓営業のミッション
            </h2>
            <div className="bg-acoe-paper border-l-4 border-acoe-gold px-8 py-6 mb-8">
              <p className="font-accent italic text-xl md:text-2xl text-acoe-charcoal leading-relaxed">
                ACOEブランドで運営できる物件を見つけ、増やしていく。
                一つひとつの契約が、ブランドの成長そのものになる。
              </p>
            </div>
            <p className="text-acoe-text leading-relaxed mb-4">
              ヒルトンやマリオットが大規模ホテルで実現しているブランド運営モデルを、東京の小規模物件で展開する。
              ACOEが目指す「最も愛されるファミリー向けホスピタリティブランド」の実現に向けて、
              このポジションはブランドの成長エンジンとなる役割です。
            </p>
            <p className="text-acoe-text leading-relaxed">
              売るのは単なる「管理サービス」ではありません。Airbnb評価4.96（2025年）、品質スコア96.9%、60日以内の250%収益保証。
              データと実績に裏付けられたブランドパートナーシップを、物件オーナーに提案していきます。
            </p>
          </div>
        </div>
      </section>

      {/* ─── WHAT YOU SELL / WHO YOU SELL TO ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="work-sell">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
            <div>
              <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">What You Propose</p>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-acoe-charcoal mb-6">何を提案するのか</h3>
              <p className="text-acoe-text leading-relaxed mb-4">
                ACOEのブランド運営パートナーシップ。物件をACOEブランドのもとで宿泊施設として運営し、
                オーナーに収益改善を実現するモデルです。
              </p>
              <ul className="space-y-3">
                {["7年の運営実績とデータに基づく収益シミュレーション", "60日以内に長期賃貸比250%の収益達成保証", "デザイン・許認可・テクノロジー・運営を一括提供", "セットアップ費用はマークアップなしの実費ベース"].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight size={16} className="text-acoe-gold mt-1 flex-shrink-0" strokeWidth={2} />
                    <span className="text-sm text-acoe-text">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Who You Talk To</p>
              <h3 className="font-heading font-bold text-xl md:text-2xl text-acoe-charcoal mb-6">誰に提案するのか</h3>
              <p className="text-acoe-text leading-relaxed mb-4">
                東京23区内の小規模物件を所有する物件オーナー。個人、法人を問わず、
                現状の収益に満足していない、または新たな収益源を探しているオーナーが対象です。
              </p>
              <ul className="space-y-3 mb-6">
                {["長期賃貸で収益が停滞しているオーナー", "空室や稼働率低下に悩むオーナー", "物件の資産価値を高めたいオーナー", "宿泊事業に興味があるが運営ノウハウがないオーナー"].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <ChevronRight size={16} className="text-acoe-gold mt-1 flex-shrink-0" strokeWidth={2} />
                    <span className="text-sm text-acoe-text">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-acoe-muted">個人オーナー / 不動産法人 / ファンド・REIT / 資産活用を検討中の方</p>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WORKFLOW ─── */}
      <section className="py-20 md:py-32 bg-acoe-bg" data-testid="work-workflow">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Workflow</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            仕事の流れ
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {WORKFLOW_STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="bg-white border border-acoe-border p-8 md:p-10 group hover:border-acoe-charcoal transition-all duration-300" data-testid={`workflow-step-${i}`}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-heading font-extrabold text-3xl text-acoe-gold/30">{step.num}</span>
                    <div className="w-10 h-10 rounded-full bg-acoe-paper flex items-center justify-center group-hover:bg-acoe-gold/10 transition-colors">
                      <Icon size={20} className="text-acoe-charcoal" strokeWidth={1.5} />
                    </div>
                  </div>
                  <h3 className="font-heading font-bold text-lg text-acoe-charcoal mb-3">{step.title}</h3>
                  <p className="text-sm text-acoe-text leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── CAREER PATH ─── */}
      <section className="py-20 md:py-32 bg-acoe-charcoal" data-testid="work-career-path">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-3">Career Path</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-4">
            キャリアパス
          </h2>
          <p className="text-white/50 mb-16 max-w-2xl">
            実績に応じてキャリアが広がります。マネジメントとプロフェッショナル、2つのトラックから自分に合った成長を選べます。
          </p>

          {/* Staff Grade */}
          <div className="mb-12">
            <div className="flex flex-col md:flex-row items-stretch gap-4">
              <div className="flex-1 border border-white/15 p-6 md:p-8">
                <p className="font-heading font-extrabold text-3xl text-white/20 mb-2">S</p>
                <h3 className="font-heading font-bold text-lg text-white">物件開拓営業</h3>
                <p className="text-sm text-white/50 mt-2">入社～</p>
              </div>
              <div className="hidden md:flex items-center">
                <ChevronRight size={20} className="text-acoe-gold" strokeWidth={2} />
              </div>
              <div className="flex-1 border border-white/15 p-6 md:p-8">
                <p className="font-heading font-extrabold text-3xl text-white/20 mb-2">SS</p>
                <h3 className="font-heading font-bold text-lg text-white">シニア物件開拓営業</h3>
                <p className="text-sm text-white/50 mt-2">実績に応じて</p>
              </div>
            </div>
          </div>

          {/* Branch indicator */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex flex-col items-center">
              <div className="w-px h-8 bg-acoe-gold/30" />
              <div className="bg-acoe-gold/10 border border-acoe-gold/30 px-6 py-3 text-center">
                <p className="text-sm text-acoe-gold font-heading font-semibold">マネジメントグレード / プロフェッショナルグレードへ分岐</p>
              </div>
              <div className="w-px h-8 bg-acoe-gold/30" />
            </div>
          </div>

          {/* Two tracks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            {/* Management Track */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-acoe-gold font-heading font-semibold mb-6 text-center">マネジメントトラック</p>
              <div className="flex flex-col gap-4">
                {[
                  { code: "M", title: "マネージャー", desc: "営業組織の戦略設計・実行管理・メンバー育成" },
                ].map((role, i) => (
                  <div key={i} className="border border-white/15 p-6 flex items-start gap-4 group hover:border-acoe-gold/40 transition-colors">
                    <p className="font-heading font-extrabold text-2xl text-acoe-gold/30 group-hover:text-acoe-gold/60 transition-colors w-12 flex-shrink-0">{role.code}</p>
                    <div>
                      <h3 className="font-heading font-bold text-base text-white">{role.title}</h3>
                      <p className="text-sm text-white/40 mt-1">{role.desc}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-center py-1">
                  <ChevronRight size={20} className="text-acoe-gold/50 rotate-90" strokeWidth={2} />
                </div>
                <div className="border border-white/15 p-6 flex items-start gap-4 group hover:border-acoe-gold/40 transition-colors">
                  <p className="font-heading font-extrabold text-2xl text-acoe-gold/30 group-hover:text-acoe-gold/60 transition-colors w-12 flex-shrink-0">SM</p>
                  <div>
                    <h3 className="font-heading font-bold text-base text-white">事業責任者 / 役員</h3>
                    <p className="text-sm text-white/40 mt-1">事業全体の意思決定・経営参画</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Track */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-acoe-gold font-heading font-semibold mb-6 text-center">プロフェッショナルトラック</p>
              <div className="flex flex-col gap-4">
                {[
                  { code: "P", title: "プロフェッショナル", desc: "物件開拓のスペシャリストとして個人の専門性を極める" },
                ].map((role, i) => (
                  <div key={i} className="border border-white/15 p-6 flex items-start gap-4 group hover:border-acoe-gold/40 transition-colors">
                    <p className="font-heading font-extrabold text-2xl text-acoe-gold/30 group-hover:text-acoe-gold/60 transition-colors w-12 flex-shrink-0">{role.code}</p>
                    <div>
                      <h3 className="font-heading font-bold text-base text-white">{role.title}</h3>
                      <p className="text-sm text-white/40 mt-1">{role.desc}</p>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-center py-1">
                  <ChevronRight size={20} className="text-acoe-gold/50 rotate-90" strokeWidth={2} />
                </div>
                <div className="border border-white/15 p-6 flex items-start gap-4 group hover:border-acoe-gold/40 transition-colors">
                  <p className="font-heading font-extrabold text-2xl text-acoe-gold/30 group-hover:text-acoe-gold/60 transition-colors w-12 flex-shrink-0">SM</p>
                  <div>
                    <h3 className="font-heading font-bold text-base text-white">事業責任者 / 役員</h3>
                    <p className="text-sm text-white/40 mt-1">専門領域から経営に参画・事業全体の意思決定</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── FIT / NOT FIT ─── */}
      <section className="py-20 md:py-32 bg-acoe-paper" data-testid="work-fit">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="text-sm tracking-[0.25em] uppercase text-acoe-muted font-heading font-semibold mb-3">Fit Check</p>
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-acoe-charcoal mb-12">
            この仕事に向いている人
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
            <div>
              <h3 className="font-heading font-semibold text-lg text-acoe-charcoal mb-6 flex items-center gap-2">
                <CheckCircle2 size={20} className="text-acoe-success" strokeWidth={1.5} /> 向いている方
              </h3>
              <ul className="space-y-4">
                {FIT_YES.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-acoe-gold mt-2 flex-shrink-0" />
                    <span className="text-acoe-text leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="font-heading font-semibold text-lg text-acoe-charcoal mb-6 flex items-center gap-2">
                <XCircle size={20} className="text-acoe-muted" strokeWidth={1.5} /> こんな方には合わないかもしれません
              </h3>
              <ul className="space-y-4">
                {FIT_NO.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-acoe-muted mt-2 flex-shrink-0" />
                    <span className="text-acoe-text leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── WHY NOW ─── */}
      <section className="py-20 md:py-32 bg-acoe-charcoal" data-testid="work-why-now">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <p className="text-sm tracking-[0.25em] uppercase text-acoe-gold font-heading font-semibold mb-3">Why Now</p>
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-white mb-8">
              なぜ今、このポジションか
            </h2>
            <p className="text-white/60 leading-relaxed mb-4">
              ACOEは7年間、代表自ら物件を見つけ、交渉し、102室まで運営を拡大してきました。
              運営力とブランドは証明済。次のフェーズは、この成功モデルをスケールさせる段階です。
            </p>
            <p className="text-white/60 leading-relaxed mb-4">
              物件開拓の専任ポジションを設けるのは、ACOE史上初めて。
              つまり、このポジションに就く方は、ACOEの成長戦略をゼロから一緒に作り上げるパートナーになります。
            </p>
            <p className="text-white/60 leading-relaxed mb-10">
              成果はインセンティブに上限なく反映され、将来の役員登用も視野に入るポジションです。
              「一緒に会社を作るフェーズ」に参加できるタイミングは、今しかありません。
            </p>
            <Link
              to="/recruit/contact"
              className="inline-flex items-center gap-2 rounded-full bg-acoe-gold text-acoe-charcoal px-8 py-4 font-heading font-semibold tracking-wide hover:bg-acoe-gold-hover transition-colors"
              data-testid="work-why-now-cta"
            >
              お問い合わせ
              <ArrowUpRight size={18} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ─── CTA BAND ─── */}
      <CTABand />
    </div>
  );
}
